/**
 * The payout-conversion ESTIMATE the withdrawal confirm step discloses (SHEET-212).
 *
 * A practice pricing in its own currency is paid out of the settlement balance; Stripe
 * converts the transfer on the destination side and keeps a conversion fee. The server
 * states the practice's currency, the two integers of `backend/modules/gateway/fx.py`'s
 * implied ratio over its own captures, and the stated fee rate
 * (`balance.limits.payoutConversion`). Everything here is arithmetic over those integers:
 * exact, half-up, the same rule as `fx.mul_div_half_up`, never a float rate.
 *
 * AN ESTIMATE, NOT A QUOTE. The rate Stripe applies at payout is unknown until it has
 * applied it; the captured history is the best statement of it this product has.
 */

/** `a * b / d` rounded half away from zero, exactly — `fx.mul_div_half_up`'s twin.
 *  BigInt because a sum of captures times an amount can leave the safe-integer range. */
export function mulDivHalfUp(a, b, d) {
  let num = BigInt(a) * BigInt(b);
  let den = BigInt(d);
  if (den === BigInt(0)) throw new RangeError("mulDivHalfUp: zero denominator");
  if (den < BigInt(0)) { num = -num; den = -den; }
  const negative = num < BigInt(0);
  const abs = negative ? -num : num;
  const q = (BigInt(2) * abs + den) / (BigInt(2) * den);
  return Number(negative ? -q : q);
}

const positiveInt = (v) => Number.isInteger(v) && v > 0;

/**
 * @param {number} sentCents  what leaves the settlement balance (after any reserve)
 * @param {?{currency:string, presentmentCents:?number, settledCents:?number, feeBps:?number}} conversion
 * @returns {null | {currency:string, feeBps:number, estimate: null | {
 *   grossCents:number, feeCents:number, netCents:number}}}
 *   null = nothing to disclose: nothing converts, or the statement is not the contract's
 *   (every converting statement carries a positive `feeBps`; one without it is read as
 *   "say nothing", the rule for every advisory `limits` field). `estimate: null` = it
 *   converts but no ratio is stated, so no local figure exists — never a guessed one.
 */
export function estimatePayoutConversion(sentCents, conversion) {
  if (!conversion || !conversion.currency || !positiveInt(conversion.feeBps)) return null;
  const { feeBps, presentmentCents: p, settledCents: s } = conversion;
  if (!positiveInt(p) || !positiveInt(s) || !Number.isInteger(sentCents) || sentCents <= 0) {
    return { currency: conversion.currency, feeBps, estimate: null };
  }
  // settled / presentment is the capture ratio, so the local figure is its inverse.
  const grossCents = mulDivHalfUp(sentCents, p, s);
  // The fee is Stripe's, taken out of the CONVERTED amount on the destination side.
  const feeCents = mulDivHalfUp(grossCents, feeBps, 10000);
  return {
    currency: conversion.currency,
    feeBps,
    estimate: { grossCents, feeCents, netCents: grossCents - feeCents },
  };
}
